import { Fragment, useState, useEffect, useRef, useCallback } from "react";
import { Formik, useFormikContext } from "formik";
import { useRoles } from "../../../hooks/useRoles";
import * as Yup from "yup";
import { resolveApiUploadUrl } from "../../../utils/mediaUrl";
import { fetchDepartmentsByParent } from "../../../services/departmentApi";

const FILE_SIZE_LIMIT = 2 * 1024 * 1024; // 2 MB
const ALLOWED_TYPES = ["image/jpeg", "image/jpg", "image/png"];
const EMPLOYEE_ROLE_ID = process.env.REACT_APP_ROLE_EMPLOYEE_ID;
const HOD_ROLE_ID = process.env.REACT_APP_ROLE_HOD_ID;

const buildSchema = (isEditMode) =>
    Yup.object().shape({
        userName: Yup.string()
            .min(3, "User Name must be at least 3 characters")
            .max(50, "User Name must not exceed 50 characters")
            .required("User Name is required"),
        email: Yup.string()
            .email("Enter a valid email address")
            .required("Email is required"),
        mobileNumber: Yup.string()
            .matches(/^\d{10}$/, "Mobile Number must be exactly 10 digits")
            .required("Mobile Number is required"),
        designation: Yup.string()
            .min(2, "Designation must be at least 2 characters")
            .required("Designation is required"),
        roleId: Yup.string().required("Role is required"),
        departmentId: Yup.string().when("roleId", {
            is: (val) => val === EMPLOYEE_ROLE_ID || val === HOD_ROLE_ID,
            then: (s) => s.required("Skill is required"),
            otherwise: (s) => s.nullable(),
        }),
        departmentChildID: Yup.string().nullable(),
        password: isEditMode
            ? Yup.string()
                .nullable()
                .test("password-strength", "Must include uppercase, lowercase, number and special character", (value) => {
                    if (!value) return true;
                    return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])/.test(value);
                })
                .test("password-min", "Password must be at least 6 characters", (value) => {
                    if (!value) return true;
                    return value.length >= 6;
                })
            : Yup.string()
                .min(6, "Password must be at least 6 characters")
                .matches(
                    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])/,
                    "Must include uppercase, lowercase, number and special character"
                )
                .required("Password is required"),
        confirmPassword: isEditMode
            ? Yup.string()
                .nullable()
                .test("passwords-match", "Passwords do not match", function (value) {
                    const { password } = this.parent;
                    if (!password) return true;
                    return value === password;
                })
            : Yup.string()
                .oneOf([Yup.ref("password"), null], "Passwords do not match")
                .required("Please confirm your password"),
        image: Yup.mixed()
            .nullable()
            .test("fileType", "Only JPG or PNG images are allowed", (value) => {
                if (!value) return true;
                if (ALLOWED_TYPES.includes(value.type)) return true;
                // Some browsers/OS leave type empty; fall back to extension
                if (!value.type && typeof value.name === "string") {
                    const ext = value.name.split(".").pop()?.toLowerCase();
                    return ["jpg", "jpeg", "png"].includes(ext);
                }
                return false;
            })
            .test("fileSize", "Image must be smaller than 2 MB", (value) => {
                if (!value) return true;
                return value.size <= FILE_SIZE_LIMIT;
            }),
    });

/**
 * Syncs Formik's roleId field once roles finish loading.
 * Required because roles arrive asynchronously — by the time the <option>
 * elements exist, Formik's controlled <select> needs to re-push the value.
 */
const RoleSync = ({ roles, rolesLoading, targetRoleId }) => {
    const { setFieldValue } = useFormikContext();
    useEffect(() => {
        if (!rolesLoading && roles.length > 0 && targetRoleId) {
            setFieldValue("roleId", targetRoleId, false);
        }
    }, [rolesLoading, roles.length, targetRoleId, setFieldValue]);
    return null;
};

/**
 * Watches departmentId (Skill) inside Formik and fetches matching child
 * departments. Never resets departmentChildID — resets are handled by the
 * Skill select's onChange so the saved value is preserved on edit load.
 */
const ChildDeptsLoader = ({ onSkillChange }) => {
    const { values } = useFormikContext();

    useEffect(() => {
        onSkillChange(values.departmentId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values.departmentId]);

    return null;
};

const UserMgmtForm = ({
    initialValues,
    onSubmit,
    onCancel,
    isEditMode,
    externalSubmitting,
    departments = [],
    depsLoading = false,
    depsError = null,
}) => {
    // Load roles inside the form so pre-selection never depends on parent timing
    const { roles, loading: rolesLoading } = useRoles();

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const fileInputRef = useRef(null);
    const [imagePreview, setImagePreview] = useState(() =>
        resolveApiUploadUrl(initialValues?.imageUrl, "users")
    );

    // Child departments: populated dynamically when a Skill is selected
    const [childDepts, setChildDepts] = useState([]);
    const [childDepsLoading, setChildDepsLoading] = useState(false);

    const handleSkillChange = useCallback(async (skillId) => {
        if (!skillId) { setChildDepts([]); return; }
        setChildDepsLoading(true);
        try {
            const data = await fetchDepartmentsByParent(skillId);
            setChildDepts(data);
        } catch {
            setChildDepts([]);
        } finally {
            setChildDepsLoading(false);
        }
    }, []);

    useEffect(() => {
        setImagePreview(resolveApiUploadUrl(initialValues?.imageUrl, "users"));
    }, [initialValues?.imageUrl]);

    const schema = buildSchema(isEditMode);

    const defaultValues = {
        userName: "",
        email: "",
        mobileNumber: "",
        designation: "",
        roleId: "",
        departmentId: "",
        departmentChildID: "",
        imageUrl: "",
        ...initialValues,
        // Always reset sensitive/file fields — never pre-fill from server
        password: "",
        confirmPassword: "",
        image: null,
    };

    const handleImageChange = (e, setFieldValue) => {
        const file = e.currentTarget.files?.[0];
        if (file) {
            setFieldValue("image", file);
            const reader = new FileReader();
            reader.onload = () => setImagePreview(reader.result);
            reader.readAsDataURL(file);
        } else {
            setFieldValue("image", null);
            setImagePreview(resolveApiUploadUrl(initialValues?.imageUrl, "users"));
        }
    };

    const fieldClass = (touched, error) =>
        `form-group mb-3 ${touched ? (error ? "is-invalid" : "is-valid") : ""}`;

    const errBlock = (touched, error) =>
        touched && error ? (
            <div
                className="invalid-feedback animated fadeInUp"
                style={{ display: "block" }}
            >
                {error}
            </div>
        ) : null;

    return (
        <Fragment>
            <Formik
                initialValues={defaultValues}
                enableReinitialize
                validationSchema={schema}
                onSubmit={onSubmit}
            >
                {({
                    values,
                    errors,
                    touched,
                    status,
                    handleChange,
                    handleBlur,
                    handleSubmit,
                    isSubmitting,
                    resetForm,
                    setFieldValue,
                    setFieldTouched,
                }) => {
                    const selectedRoleName = (
                        roles.find((r) => r.id === values.roleId)?.name || ""
                    ).toLowerCase();
                    const isEmployee =
                        values.roleId === EMPLOYEE_ROLE_ID ||
                        selectedRoleName.includes("employee");
                    const isHod =
                        values.roleId === HOD_ROLE_ID ||
                        selectedRoleName.includes("head") ||
                        selectedRoleName.includes("hod");
                    const showSkillDept = isEmployee || isHod;

                    const handleRoleChange = (e) => {
                        handleChange(e);
                        const newRoleName = (
                            roles.find((r) => r.id === e.target.value)?.name || ""
                        ).toLowerCase();
                        const newIsEmployee =
                            e.target.value === EMPLOYEE_ROLE_ID ||
                            newRoleName.includes("employee");
                        const newIsHod =
                            e.target.value === HOD_ROLE_ID ||
                            newRoleName.includes("head") ||
                            newRoleName.includes("hod");
                        if (!newIsEmployee && !newIsHod) {
                            setFieldValue("departmentId", "");
                            setFieldValue("departmentChildID", "");
                        }
                    };

                    const handleSkillSelectChange = (e) => {
                        handleChange(e);
                        setFieldValue("departmentChildID", "");
                    };

                    return (
                        <form onSubmit={handleSubmit}>
                            {/* Server-side error (e.g. email already exists) */}
                            {status && (
                                <div className="alert alert-danger d-flex align-items-center gap-2 mb-3" role="alert">
                                    <i className="fa fa-exclamation-circle" />
                                    <span>{status}</span>
                                </div>
                            )}
                            {/* Re-sync roleId once roles finish loading asynchronously */}
                            <RoleSync
                                roles={roles}
                                rolesLoading={rolesLoading}
                                targetRoleId={defaultValues.roleId}
                            />
                            {/* Load child departments whenever Skill selection changes */}
                            <ChildDeptsLoader onSkillChange={handleSkillChange} />
                            <div className="row">
                                {/* ── Column 1 ── */}
                                <div className="col-xl-6 col-lg-6">

                                    {/* User Name */}
                                    <div className={fieldClass(touched.userName, errors.userName)}>
                                        <label className="text-label">
                                            User Name <span className="text-danger">*</span>
                                        </label>
                                        <div className="input-group">
                                            <span className="input-group-text">
                                                <i className="fa fa-user" />
                                            </span>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="userName"
                                                placeholder="Enter full name"
                                                value={values.userName}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                            />
                                            {errBlock(touched.userName, errors.userName)}
                                        </div>
                                    </div>


                                    {/* Mobile Number */}
                                    <div className={fieldClass(touched.mobileNumber, errors.mobileNumber)}>
                                        <label className="text-label">
                                            Mobile Number <span className="text-danger">*</span>
                                        </label>
                                        <div className="input-group">
                                            <span className="input-group-text">
                                                <i className="fa fa-phone" />
                                            </span>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="mobileNumber"
                                                placeholder="10-digit mobile number"
                                                value={values.mobileNumber}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                maxLength={10}
                                            />
                                            {errBlock(touched.mobileNumber, errors.mobileNumber)}
                                        </div>
                                    </div>


                                    {/* Designation */}
                                    <div className={fieldClass(touched.designation, errors.designation)}>
                                        <label className="text-label">
                                            Designation <span className="text-danger">*</span>
                                        </label>
                                        <div className="input-group">
                                            <span className="input-group-text">
                                                <i className="fa fa-briefcase" />
                                            </span>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="designation"
                                                placeholder="Enter designation"
                                                value={values.designation}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                            />
                                            {errBlock(touched.designation, errors.designation)}
                                        </div>
                                    </div>

                                    {/* Role */}
                                    <div className={fieldClass(touched.roleId, errors.roleId)}>
                                        <label className="text-label">
                                            Role <span className="text-danger">*</span>
                                        </label>
                                        <div className="input-group">
                                            <span className="input-group-text">
                                                <i className="fa fa-id-badge" />
                                            </span>
                                            <select
                                                className="form-control"
                                                name="roleId"
                                                value={values.roleId}
                                                onChange={handleRoleChange}
                                                onBlur={handleBlur}
                                                disabled={rolesLoading}
                                            >
                                                <option value="">
                                                    {rolesLoading ? "Loading roles..." : "Select a role"}
                                                </option>
                                                {roles.map((role) => (
                                                    <option key={role.id} value={role.id}>
                                                        {role.name}
                                                    </option>
                                                ))}
                                            </select>
                                            {errBlock(touched.roleId, errors.roleId)}
                                        </div>
                                    </div>

                                    {/* Skill — visible only when Employee or HOD role is selected */}
                                    {showSkillDept && (
                                        <div className={fieldClass(touched.departmentId, errors.departmentId)}>
                                            <label className="text-label">
                                                Skill <span className="text-danger">*</span>
                                            </label>
                                            <div className="input-group">
                                                <span className="input-group-text">
                                                    <i className="fa fa-building" />
                                                </span>
                                                <select
                                                    className="form-control"
                                                    name="departmentId"
                                                    value={values.departmentId}
                                                    onChange={handleSkillSelectChange}
                                                    onBlur={handleBlur}
                                                    disabled={depsLoading || !!depsError}
                                                >
                                                    <option value="">
                                                        {depsLoading
                                                            ? "Loading Skills..."
                                                            : depsError
                                                                ? "Unable to load skills"
                                                                : departments.length === 0
                                                                    ? "No skills found"
                                                                    : "Select a skill"}
                                                    </option>
                                                    {!depsError &&
                                                        departments.map((dept) => (
                                                            <option key={dept._id} value={dept._id}>
                                                                {dept.departmentName}
                                                            </option>
                                                        ))}
                                                </select>
                                                {depsError && (
                                                    <div
                                                        className="invalid-feedback animated fadeInUp"
                                                        style={{ display: "block" }}
                                                    >
                                                        Unable to load Skills. Please refresh and try again.
                                                    </div>
                                                )}
                                                {errBlock(touched.departmentId, errors.departmentId)}
                                            </div>
                                        </div>
                                    )}

                                    {/* Department — visible when Skill is selected and has child departments */}
                                    {showSkillDept && (
                                        <div className="form-group mb-3">
                                            <label className="text-label">
                                                Department{" "}
                                                <small className="text-muted">(optional)</small>
                                            </label>
                                            <div className="input-group">
                                                <span className="input-group-text">
                                                    <i className="fa fa-sitemap" />
                                                </span>
                                                <select
                                                    className="form-control"
                                                    name="departmentChildID"
                                                    value={values.departmentChildID || ""}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    disabled={!values.departmentId || childDepsLoading}
                                                >
                                                    <option value="">
                                                        {!values.departmentId
                                                            ? "Select a skill first"
                                                            : childDepsLoading
                                                                ? "Loading..."
                                                                : childDepts.length === 0
                                                                    ? "No departments available"
                                                                    : "Select Department"}
                                                    </option>
                                                    {childDepts.map((dept) => (
                                                        <option key={dept._id} value={dept._id}>
                                                            {dept.departmentName}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* ── Column 2 ── */}
                                <div className="col-xl-6 col-lg-6">
                                    {/* Email */}
                                    <div className={fieldClass(touched.email, errors.email)}>
                                        <label className="text-label">
                                            Email <span className="text-danger">*</span>
                                        </label>
                                        <div className="input-group">
                                            <span className="input-group-text">
                                                <i className="fa fa-envelope" />
                                            </span>
                                            <input
                                                type="email"
                                                className="form-control"
                                                name="email"
                                                placeholder="Enter email address"
                                                value={values.email}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                            />
                                            {errBlock(touched.email, errors.email)}
                                        </div>
                                    </div>

                                    {/* Password */}
                                    <div className={fieldClass(touched.password, errors.password)}>
                                        <label className="text-label">
                                            Password{" "}
                                            {!isEditMode && <span className="text-danger">*</span>}
                                            {isEditMode && (
                                                <small className="text-muted"> (leave blank to keep current)</small>
                                            )}
                                        </label>
                                        <div className="input-group">
                                            <span className="input-group-text">
                                                <i className="fa fa-lock" />
                                            </span>
                                            <input
                                                type={showPassword ? "text" : "password"}
                                                className="form-control"
                                                name="password"
                                                placeholder={
                                                    isEditMode
                                                        ? "Leave blank to keep current"
                                                        : "Enter password"
                                                }
                                                value={values.password}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                            />
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary py-0"
                                                tabIndex={-1}
                                                onClick={() => setShowPassword((v) => !v)}
                                            >
                                                <i className={`fa ${showPassword ? "fa-eye-slash" : "fa-eye"}`} />
                                            </button>
                                            {errBlock(touched.password, errors.password)}
                                        </div>
                                    </div>

                                    {/* Confirm Password */}
                                    <div className={fieldClass(touched.confirmPassword, errors.confirmPassword)}>
                                        <label className="text-label">
                                            Confirm Password{" "}
                                            {!isEditMode && <span className="text-danger">*</span>}
                                        </label>
                                        <div className="input-group">
                                            <span className="input-group-text">
                                                <i className="fa fa-lock" />
                                            </span>
                                            <input
                                                type={showConfirm ? "text" : "password"}
                                                className="form-control"
                                                name="confirmPassword"
                                                placeholder="Re-enter password"
                                                value={values.confirmPassword}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                            />
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary py-0"
                                                tabIndex={-1}
                                                onClick={() => setShowConfirm((v) => !v)}
                                            >
                                                <i className={`fa ${showConfirm ? "fa-eye-slash" : "fa-eye"}`} />
                                            </button>
                                            {errBlock(touched.confirmPassword, errors.confirmPassword)}
                                        </div>
                                    </div>

                                    {/* Image Upload */}
                                    <div className={fieldClass(touched.image, errors.image)}>
                                        <label className="text-label">
                                            Profile Image{" "}
                                            <small className="text-muted">(JPG / PNG, max 2 MB, optional)</small>
                                        </label>
                                        <div className="input-group">
                                            <span className="input-group-text">
                                                <i className="fa fa-image" />
                                            </span>
                                            <input
                                                ref={fileInputRef}
                                                type="file"
                                                className="form-control"
                                                accept="image/jpeg,image/jpg,image/png"
                                                onChange={(e) => {
                                                    handleImageChange(e, setFieldValue);
                                                    setFieldTouched("image", true);
                                                }}
                                            />
                                            {errBlock(touched.image, errors.image)}
                                        </div>

                                        {/* Image Preview */}
                                        {imagePreview && (
                                            <div className="mt-2 d-flex align-items-center gap-3">
                                                <img
                                                    src={
                                                        resolveApiUploadUrl(imagePreview, "users") ||
                                                        imagePreview
                                                    }
                                                    alt="Preview"
                                                    style={{
                                                        width: 80,
                                                        height: 80,
                                                        objectFit: "cover",
                                                        borderRadius: "50%",
                                                        border: "2px solid #dee2e6",
                                                    }}
                                                />
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={() => {
                                                        setFieldValue("image", null);
                                                        setFieldValue(
                                                            "imageUrl",
                                                            initialValues?.imageUrl || ""
                                                        );
                                                        setImagePreview(
                                                            resolveApiUploadUrl(
                                                                initialValues?.imageUrl,
                                                                "users"
                                                            )
                                                        );
                                                        if (fileInputRef.current) fileInputRef.current.value = "";
                                                    }}
                                                >
                                                    <i className="fa fa-times me-1" /> Remove
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    {/* Actions */}
                                    <div className="form-group mb-0 mt-4 d-flex gap-2 flex-wrap">
                                        <button
                                            type="submit"
                                            className="btn btn-primary py-2"
                                            disabled={isSubmitting || externalSubmitting}
                                        >
                                            {isSubmitting || externalSubmitting ? (
                                                <>
                                                    <span
                                                        className="spinner-border spinner-border-sm me-2"
                                                        role="status"
                                                        aria-hidden="true"
                                                    />
                                                    {isEditMode ? "Updating..." : "Saving..."}
                                                </>
                                            ) : isEditMode ? (
                                                "Update User"
                                            ) : (
                                                "Add User"
                                            )}
                                        </button>
                                        {!isEditMode && (
                                            <button
                                                type="button"
                                                className="btn btn-outline-secondary py-2"
                                                onClick={() => {
                                                    resetForm();
                                                    setImagePreview(
                                                        resolveApiUploadUrl(
                                                            initialValues?.imageUrl,
                                                            "users"
                                                        )
                                                    );
                                                    if (fileInputRef.current) fileInputRef.current.value = "";
                                                }}
                                            >
                                                Reset
                                            </button>
                                        )}
                                        {onCancel && (
                                            <button
                                                type="button"
                                                className="btn btn-light py-2"
                                                onClick={onCancel}
                                            >
                                                Cancel
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </form>
                    );
                }}
            </Formik>
        </Fragment>
    );
};

export default UserMgmtForm;
